/**
 * Pure cart model for the PARENT family-ordering surface (/orders/family).
 *
 * Mirrors the SCHOOL_ADMIN cart (`@/lib/school-orders/cart`) but reshaped for
 * the parent context: where the school cart keys lines by grade-range column,
 * the family cart keys them by **child** (the household student a line is for).
 * Lives in `lib/` (no React, no `@/api` runtime imports) so the Zustand store
 * and the unit tests can both pull it in without dragging react-query into the
 * test process.
 *
 * The cart is a flat list of lines keyed by
 * `${date}::${mealtime}::${studentMemberId}::${menuItemId}`, and checkout groups
 * them back into one OrderBatch per `(serviceDate, mealtime)` — the unit the
 * server's `submitCart` enforces (one `FAMILY_DIRECT` batch per group).
 */

import type { Mealtime } from '@/api/school-orders'
import { compareMealtimes } from '@/lib/mealtime'

/**
 * One quantity entry in the family cart. The column is the **child**
 * (`AccountMember.id` → `studentMemberId`); `studentUserId` is the underlying
 * `User.id` the submit payload assigns the line to (`targetUserId`).
 *
 * `unitPriceCents` is the menu item's `customerPriceCents` captured at add time
 * so checkout can show a pre-submit subtotal without a priced cart-read
 * endpoint. Null when the menu item carries no price.
 */
export interface FamilyCartLine {
  /** `${date}::${mealtime}::${studentMemberId}::${menuItemId}` */
  key: string
  locationId: string
  organizationId: string
  /** `YYYY-MM-DD` */
  date: string
  mealtime: Mealtime
  /** `AccountMember.id` — the child column this line sits under. */
  studentMemberId: string
  /** `User.id` — the student the line is assigned to (submit `targetUserId`). */
  studentUserId: string
  /** Portion tier (`GradeRange.id`); denormalised for the submit payload. */
  gradeRangeId: string | null
  menuItemId: string
  name: string
  imageUri: string | null
  unitPriceCents: number | null
  currency: string | null
  quantity: number
  /**
   * Doctor's-note special-diet accommodation this line orders against, when the
   * item is a gated special-diet item. Threaded into the submit payload so the
   * server enforces approval + per-student daily cap. Absent for normal lines.
   */
  specialDietAccommodationId?: string | null
}

/**
 * `${date}::${mealtime}::${studentMemberId}::${menuItemId}` — with an optional
 * `::${specialDietAccommodationId}` suffix so a special-diet line never collides
 * with a normal line for the same menu item (they carry different enforcement).
 * Normal lines omit the suffix and keep their historical key unchanged.
 */
export function cartLineKey(
  date: string,
  mealtime: Mealtime,
  studentMemberId: string,
  menuItemId: string,
  specialDietAccommodationId?: string | null,
): string {
  const base = `${date}::${mealtime}::${studentMemberId}::${menuItemId}`
  return specialDietAccommodationId ? `${base}::${specialDietAccommodationId}` : base
}

/** `${date}::${mealtime}` — the group key checkout submits one batch per. */
export function groupKey(date: string, mealtime: Mealtime): string {
  return `${date}::${mealtime}`
}

/**
 * Insert / update / remove a line in an immutable line list. A quantity of
 * `<= 0` removes the line. Returns a new array; the input is never mutated.
 */
export function upsertLine(lines: FamilyCartLine[], line: FamilyCartLine): FamilyCartLine[] {
  const idx = lines.findIndex((l) => l.key === line.key)
  if (line.quantity <= 0) {
    if (idx === -1) {
      return lines
    }
    return [...lines.slice(0, idx), ...lines.slice(idx + 1)]
  }
  if (idx === -1) {
    return [...lines, line]
  }
  const next = [...lines]
  next[idx] = line
  return next
}

/** Total quantity across every line. */
export function totalQty(lines: FamilyCartLine[]): number {
  return lines.reduce((sum, l) => sum + l.quantity, 0)
}

/**
 * Pre-submit subtotal in cents — sum of `unitPriceCents * quantity` over lines
 * that carry a price. Returns `null` when NO line has a price (so the caller
 * can hide the dollar figure entirely rather than show $0.00).
 */
export function subtotalCents(lines: FamilyCartLine[]): number | null {
  let total = 0
  let priced = false
  for (const l of lines) {
    if (l.unitPriceCents != null) {
      total += l.unitPriceCents * l.quantity
      priced = true
    }
  }
  return priced ? total : null
}

/** A `(date, mealtime)` checkout group — one OrderBatch per group. */
export interface CheckoutGroup {
  date: string
  mealtime: Mealtime
  lines: FamilyCartLine[]
}

/**
 * Group cart lines into the `(date, mealtime)` batches `submitCart` creates one
 * of. Sorted by date then canonical mealtime order so the submit loop is
 * deterministic.
 */
export function buildCheckoutPlan(lines: FamilyCartLine[]): CheckoutGroup[] {
  const groups = new Map<string, CheckoutGroup>()
  for (const line of lines) {
    if (line.quantity <= 0) {
      continue
    }
    const key = groupKey(line.date, line.mealtime)
    const group = groups.get(key)
    if (group) {
      group.lines.push(line)
    } else {
      groups.set(key, { date: line.date, mealtime: line.mealtime, lines: [line] })
    }
  }
  return [...groups.values()].sort((a, b) => a.date.localeCompare(b.date) || compareMealtimes(a.mealtime, b.mealtime))
}
